import React from 'react';
import { motion } from 'framer-motion';
import { Landmark, Building, ShoppingCart, Home, Briefcase, Shield, Plane, Users } from 'lucide-react';

const industries = [
  { icon: Landmark, name: 'Financial Institutions', desc: 'Banking integrations, payment rails, compliance infrastructure for banks, credit unions, and regulated lenders.' },
  { icon: Building, name: 'Hotels & Hospitality', desc: 'Cloudbeds integration, local bank processing, payment automation built for Caribbean hospitality operators.' },
  { icon: ShoppingCart, name: 'Retail & eCommerce', desc: 'Turnkey online stores with Visa, Scotiabank, SunCash, and Plug\'n Pay checkout out of the box.' },
  { icon: Home, name: 'Real Estate', desc: 'Property management, probate, transaction workflows, and estate operations via APC Estate Flow.' },
  { icon: Briefcase, name: 'Professional Services', desc: 'Law firms, accountancies, advisors — secure communications, document workflows, and billing.' },
  { icon: Shield, name: 'Government & Public Sector', desc: 'Compliance-ready payment integrations and secure communication channels for public-sector clients.' },
  { icon: Plane, name: 'Tourism', desc: 'Booking integrations, multi-currency processing, and regional payment partnerships for tourism operators.' },
  { icon: Users, name: 'SMEs & Entrepreneurs', desc: 'Business formation, eCommerce launch, payment enablement, and growth financing — all under one ecosystem.' },
];

const IndustriesServed = () => {
  return (
    <section id="industries" className="relative py-24 md:py-32 bg-[#f7f7f5]" data-testid="industries-served">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-3 mb-4">
            <div className="h-[1px] w-8 bg-[#C9A227]" />
            <span className="text-[#C9A227] text-[10px] tracking-[0.4em] uppercase font-semibold">Sectors</span>
            <div className="h-[1px] w-8 bg-[#C9A227]" />
          </span>
          <h2 className="text-[#0a1628] text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Industries <span className="text-[#C9A227] italic">We Serve</span>
          </h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto font-light">
            From financial institutions to entrepreneurs — SAG infrastructure powers organizations across every key sector.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {industries.map((ind, i) => {
            const Icon = ind.icon;
            return (
              <motion.div
                key={ind.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -10, transition: { type: 'spring', stiffness: 300, damping: 20 } }}
                className="group bg-white border border-gray-200 hover:border-[#C9A227]/50 p-6 transition-all hover:shadow-[0_24px_60px_-16px_rgba(10,22,40,0.22)] relative overflow-hidden"
                data-testid={`industry-${ind.name.toLowerCase().replace(/\s+|&/g, '-')}`}
              >
                {/* Gold accent line that slides in on hover */}
                <div className="absolute top-0 left-0 h-[2px] w-0 group-hover:w-full bg-[#C9A227] transition-all duration-500" />

                <motion.div
                  whileHover={{ rotate: 10, scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  className="w-12 h-12 bg-[#0a1628]/5 group-hover:bg-[#C9A227]/15 flex items-center justify-center mb-4 transition-colors"
                >
                  <Icon className="w-5 h-5 text-[#0a1628] group-hover:text-[#C9A227] transition-colors" />
                </motion.div>
                <h3 className="text-[#0a1628] text-base font-medium mb-2">{ind.name}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{ind.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default IndustriesServed;
